import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import API_URL, { BASE_URL } from '../../../config';
import './ContentPickerModal.css';

function ContentPickerModal({
    isOpen,
    onClose,
    onSave,
    selectedTagIds,
    tags,
    ruleId,
    existingOrder = []
}) {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedContents, setSelectedContents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal açıldığında içerikleri yükle
    useEffect(() => {
        if (isOpen && selectedTagIds.length > 0) {
            loadContents();
        }
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen, selectedTagIds]);

    // Mevcut sıralamayı yükle
    useEffect(() => {
        if (isOpen && existingOrder && existingOrder.length > 0) {
            setSelectedContents(existingOrder);
        } else if (isOpen) {
            setSelectedContents([]);
        }
    }, [isOpen, existingOrder]);

    const loadContents = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(`${API_URL}/content-library`, {
                headers: { 'Authorization': `Bearer ${token}` },
                params: {
                    tagIds: selectedTagIds.join(',')
                }
            });

            const allContents = response.data.contents || [];
            setContents(allContents);

        } catch (error) {
            console.error('İçerikler yüklenemedi:', error);
            toast.error('İçerikler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // İçerik seç/kaldır
    const toggleContent = (content) => {
        setSelectedContents(prev => {
            const exists = prev.find(c => c.id === content.id);
            if (exists) {
                return prev.filter(c => c.id !== content.id);
            } else {
                return [...prev, content];
            }
        });
    };

    // Sıra değiştir
    const moveItem = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= selectedContents.length) return;

        setSelectedContents(prev => {
            const newList = [...prev];
            [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
            return newList;
        });
    };

    // Kaydet
    const handleSave = () => {
        if (selectedContents.length === 0) {
            toast.error('En az bir içerik seçmelisiniz');
            return;
        }

        setSaving(true);

        const orderData = selectedContents.map((content, index) => ({
            content_id: content.id,
            order_index: index + 1
        }));

        onSave(orderData, selectedContents);
        toast.success(`${selectedContents.length} içerik sıralandı`);
        setSaving(false);
        onClose();
    };

    // Filtreleme
    const filteredContents = useMemo(() => {
        if (!searchTerm.trim()) return contents;
        const term = searchTerm.toLowerCase();
        return contents.filter(c =>
            (c.title && c.title.toLowerCase().includes(term)) ||
            (c.content && c.content.toLowerCase().includes(term))
        );
    }, [contents, searchTerm]);

    // Medya parse
    const parseMediaData = (content) => {
        let mediaItems = [];
        try {
            if (content.media_path) {
                if (content.media_path.startsWith('[')) {
                    const paths = JSON.parse(content.media_path);
                    const types = content.media_type ? JSON.parse(content.media_type) : [];
                    mediaItems = paths.map((path, index) => ({
                        url: `${BASE_URL}/uploads/${path}`,
                        type: types[index] || 'image'
                    }));
                } else {
                    mediaItems = [{
                        url: `${BASE_URL}/uploads/${content.media_path}`,
                        type: content.media_type || 'image'
                    }];
                }
            }
        } catch (e) {
            console.error('Media parse error:', e);
            if (content.media_path) {
                mediaItems = [{
                    url: `${BASE_URL}/uploads/${content.media_path}`,
                    type: content.media_type || 'image'
                }];
            }
        }
        return mediaItems;
    };

    // Seçili mi kontrolü
    const isSelected = (contentId) => selectedContents.some(c => c.id === contentId);
    const getSelectionIndex = (contentId) => selectedContents.findIndex(c => c.id === contentId);

    if (!isOpen) return null;

    return (
        <div className="cpm-overlay" onClick={onClose}>
            <div className="cpm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="cpm-header">
                    <div>
                        <h2>İçerik Seç ve Sırala</h2>
                        <p>Otomasyonda paylaşılacak içerikleri seçin</p>
                    </div>
                    <button className="cpm-close-btn" onClick={onClose}>✕</button>
                </div>

                {/* Etiketler */}
                <div className="cpm-tags-bar">
                    <span>Etiketler:</span>
                    {selectedTagIds.map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        return tag ? (
                            <span key={tag.id} className="cpm-tag" style={{ backgroundColor: tag.color }}>
                                {tag.name}
                            </span>
                        ) : null;
                    })}
                </div>

                {/* Arama ve Bilgi */}
                <div className="cpm-toolbar">
                    <div className="cpm-search-box">
                        <span className="cpm-search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="İçerik ara..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="cpm-search-clear" onClick={() => setSearchTerm('')}>✕</button>
                        )}
                    </div>
                    <div className="cpm-stats">
                        <span>📚 {filteredContents.length} içerik</span>
                        <span>✅ {selectedContents.length} seçili</span>
                    </div>
                </div>

                {/* İçerik Alanı */}
                <div className="cpm-body">
                    {/* Sol - İçerik Listesi */}
                    <div className="cpm-content-area">
                        {loading ? (
                            <div className="cpm-loading">
                                <div className="cpm-spinner"></div>
                                <p>İçerikler yükleniyor...</p>
                            </div>
                        ) : filteredContents.length === 0 ? (
                            <div className="cpm-empty">
                                <span>📭</span>
                                <p>Bu etiketlere ait içerik bulunamadı</p>
                            </div>
                        ) : (
                            <div className="cpm-grid">
                                {filteredContents.map(content => {
                                    const selected = isSelected(content.id);
                                    const selIndex = getSelectionIndex(content.id);
                                    const mediaItems = parseMediaData(content);
                                    const hasVideo = mediaItems.some(m => m.type === 'video');

                                    return (
                                        <div
                                            key={content.id}
                                            className={`cpm-card ${selected ? 'selected' : ''}`}
                                            onClick={() => toggleContent(content)}
                                        >
                                            {/* Seçim Badge */}
                                            {selected && (
                                                <div className="cpm-card-badge">{selIndex + 1}</div>
                                            )}

                                            {/* Medya */}
                                            <div className="cpm-card-media">
                                                {mediaItems.length > 0 ? (
                                                    <>
                                                        {mediaItems[0].type === 'video' ? (
                                                            <video src={mediaItems[0].url} muted preload="metadata" />
                                                        ) : (
                                                            <img src={mediaItems[0].url} alt="" />
                                                        )}
                                                        {hasVideo && <span className="cpm-video-badge">▶ Video</span>}
                                                        {mediaItems.length > 1 && (
                                                            <span className="cpm-multi-badge">+{mediaItems.length - 1}</span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="cpm-no-media">📄</div>
                                                )}
                                            </div>

                                            {/* Bilgi */}
                                            <div className="cpm-card-body">
                                                <h4>{content.title || 'Başlıksız'}</h4>
                                                <p>{content.content?.substring(0, 80) || ''}{content.content?.length > 80 ? '...' : ''}</p>
                                            </div>

                                            {/* Etiketler */}
                                            {content.content_tags && content.content_tags.length > 0 && (
                                                <div className="cpm-card-tags">
                                                    {content.content_tags.slice(0, 3).map(tag => (
                                                        <span key={tag.id} style={{ backgroundColor: tag.color }}>
                                                            {tag.name}
                                                        </span>
                                                    ))}
                                                    {content.content_tags.length > 3 && (
                                                        <span className="cpm-more-tags">+{content.content_tags.length - 3}</span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Checkbox */}
                                            <div className={`cpm-checkbox ${selected ? 'checked' : ''}`}>
                                                {selected && <span>✓</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Sağ - Seçilen İçerikler */}
                    <div className="cpm-sidebar">
                        <div className="cpm-sidebar-header">
                            <h3>📋 Paylaşım Sırası</h3>
                            {selectedContents.length > 0 && (
                                <button className="cpm-clear-btn" onClick={() => setSelectedContents([])}>
                                    Temizle
                                </button>
                            )}
                        </div>

                        <div className="cpm-sidebar-content">
                            {selectedContents.length === 0 ? (
                                <div className="cpm-sidebar-empty">
                                    <span>👈</span>
                                    <p>Soldan içerik seçin</p>
                                    <small>Seçim sırası = Paylaşım sırası</small>
                                </div>
                            ) : (
                                <div className="cpm-order-list">
                                    {selectedContents.map((content, index) => {
                                        const mediaItems = parseMediaData(content);

                                        return (
                                            <div key={content.id} className="cpm-order-item">
                                                <span className="cpm-order-num">{index + 1}</span>

                                                <div className="cpm-order-thumb">
                                                    {mediaItems.length > 0 ? (
                                                        mediaItems[0].type === 'video' ? (
                                                            <span>🎬</span>
                                                        ) : (
                                                            <img src={mediaItems[0].url} alt="" />
                                                        )
                                                    ) : (
                                                        <span>📄</span>
                                                    )}
                                                </div>

                                                <span className="cpm-order-title">{content.title || 'Başlıksız'}</span>

                                                <div className="cpm-order-actions">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); moveItem(index, -1); }}
                                                        disabled={index === 0}
                                                    >↑</button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); moveItem(index, 1); }}
                                                        disabled={index === selectedContents.length - 1}
                                                    >↓</button>
                                                    <button
                                                        className="remove"
                                                        onClick={(e) => { e.stopPropagation(); toggleContent(content); }}
                                                    >✕</button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="cpm-footer">
                    <span>{selectedContents.length > 0 ? `${selectedContents.length} içerik seçildi` : 'Henüz içerik seçilmedi'}</span>
                    <div className="cpm-footer-btns">
                        <button className="cpm-btn-cancel" onClick={onClose}>İptal</button>
                        <button
                            className="cpm-btn-save"
                            onClick={handleSave}
                            disabled={saving || selectedContents.length === 0}
                        >
                            {saving ? 'Kaydediliyor...' : `Kaydet (${selectedContents.length})`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContentPickerModal;